/**
 * GenerateSheet — bottom sheet that matches the mobile mockup:
 *   • Module row (Gym / Home / Yoga / Zumba) — black pill for active
 *   • GOAL (FROM PROFILE) — locked chip, read from user.goal
 *   • LEVEL (Beginner / Intermediate / Advanced) — black pill for active
 *   • DURATION (15 / 30 / 45 / 60 min) — black pill for active
 *   • FOCUS (Upper / Lower / Push / Pull / Cardio / Mobility) — single select
 *   • Cyan "Generate Plan" pill CTA
 *
 * After a successful generation the sheet closes and the user is routed to
 * `/workouts/plan/:planId` (the full-screen plan preview).
 *
 * Yoga & Zumba modules currently show a friendly "coming soon" toast.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, Lock } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

const MODULES = [
  { id: "gym", label: "Gym" },
  { id: "home", label: "Home" },
  { id: "yoga", label: "Yoga" },
  { id: "zumba", label: "Zumba" },
];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const DURATIONS = [15, 30, 45, 60];
const FOCUS = ["Upper", "Lower", "Push", "Pull", "Cardio", "Mobility"];

// Fallback equipment presets — Backend uses `focus` to build muscle groups.
const EQUIP_FOR_MODULE = {
  gym: ["dumbbells", "barbell", "cable", "machine"],
  home: ["bodyweight"],
  yoga: ["bodyweight"],
  zumba: ["bodyweight"],
};

export default function GenerateSheet({ open, onClose, defaultModule }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profileGoal = user?.goal || "Stay Fit";

  const [module, setModule] = useState(defaultModule || "gym");
  const [level, setLevel] = useState("Intermediate");
  const [duration, setDuration] = useState(45);
  const [focus, setFocus] = useState("Upper");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setModule(defaultModule || "gym");
      setLoading(false);
    }
  }, [open, defaultModule]);

  const generate = async () => {
    if (!user) {
      toast.message("Please sign in to generate a plan");
      navigate("/login");
      return;
    }
    if (module === "yoga" || module === "zumba") {
      toast.info(`${module[0].toUpperCase() + module.slice(1)} sessions launching soon`, {
        description: "Choose Gym or Home for now — full Yoga & Zumba libraries are on the way.",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/workout/generate", {
        location: module,
        level: level.toLowerCase(),
        duration_minutes: duration,
        focus: focus.toLowerCase(),
        goal: profileGoal,
        equipment: EQUIP_FOR_MODULE[module] || ["bodyweight"],
        muscles: [],
      });
      onClose();
      setTimeout(() => navigate(`/workouts/plan/${data.plan_id}`), 80);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not generate plan";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            aria-label="Close generator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            data-testid="generate-scrim"
            className="fixed inset-0"
            style={{ background: "rgba(20,18,15,0.45)", zIndex: 70 }}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="fixed left-0 right-0 bottom-0"
            style={{
              background: "#FFFFFF",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              maxHeight: "92vh",
              overflowY: "auto",
              zIndex: 71,
              borderTop: "1px solid #E2E0DC",
              boxShadow: "0 -12px 40px rgba(0,0,0,0.18)",
            }}
            data-testid="generate-sheet"
          >
            <div className="max-w-2xl mx-auto p-5 pb-10">
              {/* Drag handle */}
              <div className="flex justify-center mb-3">
                <div style={{ width: 42, height: 4, background: "#D8D5D0", borderRadius: 4 }} />
              </div>

              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 800,
                      fontSize: 30,
                      color: "#1A1A1A",
                      lineHeight: 1.05,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Generate Workout
                  </h2>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, color: "#8A8884", marginTop: 4 }}>
                    AI-tailored to your goal
                  </p>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  data-testid="generate-close"
                  className="w-9 h-9 grid place-items-center rounded-full"
                  style={{ background: "#F4F3F1", border: "1px solid #E7E5E1" }}
                >
                  <X size={16} color="#1A1A1A" />
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                <Section title="Module">
                  <PillRow
                    items={MODULES.map((m) => ({ id: m.id, label: m.label }))}
                    value={module}
                    onChange={(v) => setModule(v)}
                    testidPrefix="gen-module"
                  />
                </Section>

                <Section title="Goal (from profile)">
                  <div
                    data-testid="gen-goal-locked"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      borderRadius: 100,
                      background: "#F4F3F1",
                      border: "1px solid #E7E5E1",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#1A1A1A",
                    }}
                  >
                    <Lock size={13} color="#8A8884" />
                    {profileGoal}
                  </div>
                </Section>

                <Section title="Level">
                  <PillRow
                    items={LEVELS.map((l) => ({ id: l, label: l }))}
                    value={level}
                    onChange={setLevel}
                    testidPrefix="gen-level"
                  />
                </Section>

                <Section title="Duration">
                  <PillRow
                    items={DURATIONS.map((d) => ({ id: d, label: `${d} min` }))}
                    value={duration}
                    onChange={setDuration}
                    testidPrefix="gen-duration"
                  />
                </Section>

                <Section title="Focus">
                  <PillRow
                    items={FOCUS.map((f) => ({ id: f, label: f }))}
                    value={focus}
                    onChange={setFocus}
                    testidPrefix="gen-focus"
                  />
                </Section>

                <button
                  onClick={generate}
                  disabled={loading}
                  data-testid="gen-cta"
                  className="w-full mt-3 flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{
                    background: "#00D4FF",
                    color: "#1A1A1A",
                    borderRadius: 100,
                    height: 56,
                    fontWeight: 700,
                    fontSize: 16,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    boxShadow: "0 6px 18px rgba(0,212,255,0.35)",
                  }}
                >
                  {loading ? (
                    "Crafting your plan…"
                  ) : (
                    <>
                      <Sparkles size={18} /> Generate Plan
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 11,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "#8A8884",
          fontWeight: 700,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function PillRow({ items, value, onChange, testidPrefix }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map((it) => {
        const on = value === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onChange(it.id)}
            data-testid={`${testidPrefix}-${String(it.id).toLowerCase()}`}
            style={{
              padding: "10px 20px",
              borderRadius: 100,
              border: 0,
              background: on ? "#1A1A1A" : "#F4F3F1",
              color: on ? "#F4F3F1" : "#1A1A1A",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              transition: "background 160ms ease",
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
